// Resources1.js
import { useState } from 'react';
import { BarChart2, DollarSign, Users, Package, ArrowRightCircle, TrendingUp } from 'lucide-react';
import './Resources1.css';
import { useNavigate } from 'react-router-dom';

export default function Resources1() {
  const [hoveredCase, setHoveredCase] = useState(null);
  const navigate = useNavigate();
  const handleMouseEnter = (id) => {
    setHoveredCase(id);
  };

  const handleMouseLeave = () => {
    setHoveredCase(null);
  };

  const handleNavigate = (path) => {
    navigate(path);
    window.scrollTo(0, 0);
  }
 
  return (
    <div className="resources1-container">
      {/* Decorative Elements */}
      <div className="decorative-elements">
        <div className="decorative-circle decorative-circle-1"></div>
        <div className="decorative-circle decorative-circle-2"></div>
        <div className="decorative-circle decorative-circle-3"></div>
      </div>
      {/* Hero Section */}
      <header className="resources1-hero-section">
        <div className="r1-hero-content">
          <h1 className="r1-main-title">
            How <span className="r1-brand-highlight">ThinkFinance</span> Can Be Applied
            <br />
            <span className="r1-subtitle">
              Use Cases for Financial Analytics and Forecasting
            </span>
          </h1>
          <p className="r1-intro-text">
            Strong business decisions rely on a clear view of financial performance. Financial analytics goes beyond basic reporting to uncover patterns, flag risks, and spot opportunities that traditional methods often miss.
          </p>
        </div>
      </header>

      {/* Main Content */}
      <main className="r1-main-content">
        <div className="r1-content-wrapper">
          <div className="r1-section-intro">
            <div className="r1-section-badge">
              <TrendingUp className="r1-badge-icon" />
              <span className="r1-badge-text">Strategic Insights</span>
            </div>
            <p className="r1-body-text">
              It helps businesses track spending, understand revenue, and respond quickly to change. For businesses looking to stay agile and forward-thinking, knowing how to apply financial analytics isn't optional—it's essential.
            </p>
            <p className="r1-emphasis-text">
              Below, we explore practical ways financial analytics can address common challenges and improve decision-making across your business.
            </p>

            <p className="r1-use-case-header"> USE CASES</p>
          </div>

          {/* Use Case Cards */}
          <div className="r1-use-case-grid">
            {/* Use Case 1 */}
            <div 
              className={`r1-use-case-card ${hoveredCase === 1 ? 'hovered' : ''}`}
              onMouseEnter={() => handleMouseEnter(1)}
              onMouseLeave={handleMouseLeave}
            >
              <div className="r1-card-decoration"></div>
              <div className="r1-card-content">
                <div className="r1-case-header">
                  <div className="r1-icon-container">
                    <BarChart2 className="r1-case-icon" />
                  </div>
                  <h2 className="r1-case-title">
                    Efficient Financial Review and Forecast Adjustments                    
                  </h2>
                </div>
                <p className="r1-case-text">
                  To stay aligned with financial goals, businesses need to regularly compare their actual results with their projections. Financial analytics helps quickly identify variances and uncover their causes —whether due to unexpected costs, revenue changes, or operational shifts.
                  <br /><br />
                  <span className="r1-case-highlight">By drilling down into financial statements, organizations can pinpoint discrepancies and adjust strategies and forecasts to stay on track for year-end targets.</span>
                </p>
              </div>
            </div>

            {/* Use Case 2 */}
            <div 
              className={`r1-use-case-card ${hoveredCase === 2 ? 'hovered' : ''}`}
              onMouseEnter={() => handleMouseEnter(2)}
              onMouseLeave={handleMouseLeave}
            >
              <div className="r1-card-decoration"></div>
              <div className="r1-card-content">
                <div className="r1-case-header">
                  <div className="r1-icon-container">
                    <DollarSign className="r1-case-icon" />
                  </div>
                  <h2 className="r1-case-title">
                    Improving Cost Control and Optimising Budget Accuracy
                  </h2>
                </div>
                <p className="r1-case-text">
                  Accurately distinguishing between fixed and variable costs is critical for effective financial management. Traditional definitions may not always apply to real-world financial scenarios, making it important to reassess how expenses are classified. Our financial analytics precisely make this distinction, ensuring that costs are appropriately categorized.
                  <br /><br />
                  <span className="r1-case-highlight"> By tracking variable cost forecasts, businesses can detect when actual expenses exceed budgeted amounts, highlighting areas where cost savings are unlikely.</span>
                </p>
              </div>
            </div>

            {/* Use Case 3 */}
            <div 
              className={`r1-use-case-card ${hoveredCase === 3 ? 'hovered' : ''}`}
              onMouseEnter={() => handleMouseEnter(3)}
              onMouseLeave={handleMouseLeave}
            >
              <div className="r1-card-decoration"></div>
              <div className="r1-card-content">
                <div className="r1-case-header">
                  <div className="r1-icon-container">
                    <Users className="r1-case-icon" />
                  </div>
                  <h2 className="r1-case-title">
                    Optimising Resource Allocation with Customer Segmentation
                  </h2>
                </div>
                <p className="r1-case-text">
                  Effective resource allocation begins with understanding which customer segments yield the highest returns. Our analytics solution enables businesses to differentiate between high-margin and low-margin customer groups, providing actionable insights for better decision-making.
                  <br /><br />
                  <span className="r1-case-highlight">By prioritizing customers with low costs and high margins, organizations can direct resources toward the most profitable segments, maximizing both short-term gains and long-term relationships.</span>
                </p>
              </div>
            </div>

            {/* Use Case 4 */}
            <div 
              className={`r1-use-case-card ${hoveredCase === 4 ? 'hovered' : ''}`}
              onMouseEnter={() => handleMouseEnter(4)}
              onMouseLeave={handleMouseLeave}
            >
              <div className="r1-card-decoration"></div>
              <div className="r1-card-content">
                <div className="r1-case-header">
                  <div className="r1-icon-container">
                    <Package className="r1-case-icon" />
                  </div>
                  <h2 className="r1-case-title">
                    Optimizing Product Portfolio for Growth and Profitability
                  </h2>
                </div>
                <p className="r1-case-text">
                  Understanding how each product contributes to revenue and margins is essential for refining strategy and maintaining competitiveness. Our analytics platform enables detailed performance tracking across product segments, combining historical data with trend analysis to reveal patterns that drive strategic decisions.
                  <br /><br />
                  <span className="r1-case-highlight">By aligning internal performance insights with external market dynamics and competitor benchmarks, businesses can identify high-potential products early and allocate resources accordingly. At the same time, products approaching the end of their lifecycle can be de-prioritized, ensuring investments are focused on areas with the greatest growth and profitability impact.</span>
                </p>
              </div>
            </div>
          </div>

          {/* Call to Action */}
          <div className="r1-cta-section">
            <div className="r1-cta-content">
              <h3 className="r1-cta-title">
                Use data to improve your financial planning, cost management, customer targeting, and product strategy.
              </h3>
              <p className="r1-cta-text">
                Contact us to find out how our analytics can provide the clarity you need.
              </p>
              <button className="r1-cta-button" onClick={() => handleNavigate('/contactus')}>
                <span>Contact Us</span>
                {/*<ArrowRightCircle className="r1-button-icon" />*/}
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}